import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { FilterContext } from "../FilterContextProvider";
import Select from "react-select";
import Input from "../Components/Input";
import SmallInput from "../Components/SmallInput";
import useApi from "../Api/Api";
import Button from "../Components/Button";
import InventoryLayout from "./InventoryLayout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import ReactToPrint from "react-to-print";
import { exportToCSV } from "../Utility/Csv";
import useSalesSummary from "../hooks/useSalesSummary";
import Footer from "../Components/Footer";
import EventFilter from "../Sales/EventFilter";

const ForecastInventory = () => {
  const [resultsLoading, setResultsLoading] = useState(false);
  const { filterData, setFilterData } = useContext(FilterContext);
  const api = useApi();
  const [sales, setSales] = useState([]);
  const [events, setEvents] = useState([]);
  const [alterSalesObj, setAlterSalesObj] = useState({});
  const [forecastBasis, setForecastBasis] = useState({});
  const [sortFilter, setSortFilter] = useState("per_head");
  const [categories, setCategories] = useState([]);
  const [eventFilterDate, setEventFilterDate] = useState({start_date: "", end_date: "", date_button: ""});
  const printRef = useRef();
  const [formatPrint, setFormatPrint] = useState(false);
  const {
    productsToRender,
    allProdsCategoryFilter,
    setAllProdsCategoryFilter,
    allProdsSearchFilter,
    setAllProdsSearchFilter,
    resetPagination,
  } = useSalesSummary("active");

  const { renderFooter, splitItemsList, setCurrentPage } =
    Footer(productsToRender);

  const handleFormatPrint = () => {
    setFormatPrint(!formatPrint);
  };
  
  const sortOptions = [
    {
      label: "Per head: Calculate forecast based on projected attendance",
      value: "per_head",
    },
    {
      label: "Per show: Calculate forecast based on projected number of shows",
      value: "per_show",
    },
    {
      label: "Gross sales: Calculate forecast based on projected total sales",
      value: "gross_sales",
    },
  ];

  const handleSortSelect = (option) => {
    setSortFilter(option.value);
  };

  const categoryOptions = categories.map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const handleCategorySelect = (option) => {
    setCurrentPage(1);
    resetPagination();
    setAllProdsCategoryFilter(option.value);
  };

  sales.forEach((sale) => {
    const diff =
      sale.count_in +
      sale.add +
      sale.add_2 +
      sale.add_3 +
      sale.add_4 -
      sale.count_out -
      sale.damage_quantity -
      sale.band_comp_quantity -
      sale.ctm_comp -
      sale.management_comp -
      sale.discount_quantity;
    sale.sold = diff;
    if (sale.event.attendance > 0) {
      sale.per_head = diff / sale.event.attendance;
    } else {
      sale.per_head = 0;
    }
    if (sale.event.gross_sales > 0) {
      sale.per_gross = diff / sale.event.gross_sales;
    } else {
      sale.per_gross = 0;
    }
  });

  productsToRender.forEach((prod) => {
    if (prod.size_other) {
      prod.product_size = prod.size_other;
    } else if (prod?.size?.name) {
      prod.product_size = prod.size.name;
    } else if (!prod.size) {
      prod.product_size = null;
    }
    const salesWithProdId = sales.filter(
      ({ product_id }) => prod.id === product_id
    );
    prod.per_head =
      salesWithProdId.reduce((a, b) => {
        return a + b.per_head;
      }, 0) / salesWithProdId.length;
    prod.per_show =
      salesWithProdId.reduce((a, b) => {
        return a + b.sold;
      }, 0) / salesWithProdId.length;
    prod.per_gross =
      salesWithProdId.reduce((a, b) => {
        return a + b.per_gross;
      }, 0) / salesWithProdId.length;
    prod.event_count = salesWithProdId.length;
    if (!prod.order) {
      prod.order = 0;
    }
    if (!prod.per_gross) {
      prod.per_gross = 0;
    }
  });

  const handleInputChange = ({ target: { name, value } }, product) => {
    setAlterSalesObj({
      ...alterSalesObj,
      [product.id]: {
        ...product,
        [name]: value,
      },
    });
  };

  const handleForecastBasisChange = (e) => {
    setForecastBasis({ ...forecastBasis, [e.target.id]: e.target.value });
  };

  const [eventIds, setEventIds] = useState({});
  

  const [searchTimeout, setSearchTimeout] = useState();

  const handleSearchChange = (e) => {
    setSearchTimeout(e.target.value);
  };

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setAllProdsSearchFilter(searchTimeout);
    }, 500);

    return () => clearTimeout(delaySearch);
  }, [searchTimeout, setAllProdsSearchFilter]);

  const resetFilters = () => {
    setCurrentPage(1);
    setSearchTimeout();
    setAllProdsCategoryFilter();
  };

  const clear = () => {
    setAlterSalesObj({});
    setEventIds({});
  };

  useEffect(() => {
    setResultsLoading(true);
    const queryString = Object.keys(eventIds)
      .map((id) => `event_ids[]=${id}`)
      .join("&");
    Object.keys(eventIds).length > 0 &&
      api
        .get(`/sales/forecast?artist_id=${filterData.artist_id}&${queryString}`)
        .then((r) => {
          setSales(r.data);
          setResultsLoading(false);
        });
  }, [api, filterData.artist_id, eventIds]);

  const getEvents = useCallback(() => {
    let queryParams = {};
    if (filterData.artist_id) {
      queryParams.artist_id = filterData.artist_id;
    }
    const queryString = Object.keys(queryParams)
      .map((param) => `${param}=${queryParams[param]}`)
      .join("&");
    filterData.artist_id &&
      api.get(`/events/all?${queryString}&sort=date`).then((r) => {
        setEvents(r.data);
      });
  }, [api, filterData]);

  useEffect(() => {
    filterData.artist_id && getEvents();
  }, [filterData, getEvents]);

  useEffect(() => {
    api.get(`/categories`).then((r) => {
      setCategories(r.data);
    });
  }, [api]);


  const prodsToExport = productsToRender.map((each) => {
    const product = alterSalesObj[each.id] || each;
    if (sortFilter === "per_head") {
      return {
        Name: `${product.name} - ${product.product_size}`,
        Current: product.current,
        On_order: product.order,
        Per_head: product.per_head,
        Need_per_head: product.per_head * forecastBasis.attendance,
        Order_per_head:
          product.per_head * forecastBasis.attendance -
          product.order -
          product.current,
      };
    } else if (sortFilter === "per_show") {
      return {
        Name: `${product.name} - ${product.product_size}`,
        Current: product.current,
        On_order: product.order,
        Per_show: product.per_show,
        Shows_left: product.current / product.per_show,
        Need_per_show: product.per_show * forecastBasis.number_of_shows,
        Order_per_show:
          product.per_show * forecastBasis.number_of_shows -
          product.order -
          product.current,
      };
    } else if (sortFilter === "gross_sales") {
      return {
        Name: `${product.name} - ${product.product_size}`,
        Current: product.current,
        On_order: product.order,
        Per_gross: product.per_gross,
        Need_per_gross: product.per_gross * forecastBasis.gross_sales,
        Order_per_gross:
          product.per_gross * forecastBasis.gross_sales -
          product.order -
          product.current,
      };
    }
    return null;
  });

  let count = 0;

  for (let i = 1; i < productsToRender.length; i++) {
    if (productsToRender[i - 1].name !== productsToRender[i].name) {
      count++;
    }
    productsToRender[i].productNumber = count;
  }

  const renderSelectArtistMessage = () => {
    return (
      <div className="m-8 py-12 pl-4">
        Please select an artist from the filter in order to make an adjustment
      </div>
    );
  };

  const renderAdjustTable = () => {
    return (
      <div>
        {splitItemsList.length > 0 ? (
          <div>
            <form>
              <table>
                <thead className="text-xs font-light sticky top-0 bg-white">
                  <tr className="w-full text-center border-b-2 h-11">
                    <td className="w-1/3 text-left pl-6">PRODUCT</td>
                    <td className="w-1/6">CURRENTLY AVAILABLE</td>
                    <td className="w-1/6">ON ORDER</td>
                    {sortFilter === "per_head" && (
                      <td className="w-1/6">PRODUCT SALES/HEAD</td>
                    )}
                    {sortFilter === "per_show" && (
                      <>
                        <td className="w-1/6">PRODUCT SALES/SHOW</td>
                        <td className="w-1/6">SHOWS LEFT</td>
                      </>
                    )}
                    {sortFilter === "gross_sales" && (
                      <td className="w-1/6">PRODUCT SALES/GROSS</td>
                    )}
                    <td className="w-1/6">FORECASTED NEED</td>
                    <td className="w-1/6 pr-10">ORDER</td>
                  </tr>
                </thead>
                <tbody>
                  {splitItemsList.map((product, i) => {
                    const productToRender =
                      alterSalesObj[product.id] || product;
                    return (
                      <tr
                        key={productToRender.id}
                        className={cn("border-b text-sm h-11 text-center", {
                          "bg-yellow-50": alterSalesObj[product.id],
                          "bg-gray-100":
                            splitItemsList[i].productNumber % 2 === 1,
                        })}
                      >
                        {productToRender.product_size ? (
                          <td className="text-left pl-6">
                            {productToRender.name} -{" "}
                            {productToRender.product_size}
                          </td>
                        ) : (
                          <td className="text-left pl-6">
                            {productToRender.name}
                          </td>
                        )}
                        {sortFilter === "per_head" && (
                          <>
                            <td id={`currently_available_${i}`}>
                              {productToRender.currently_available}
                            </td>
                            <td>
                              <SmallInput
                                name="order"
                                id={`order_${i}`}
                                onChange={(event) =>
                                  handleInputChange(event, productToRender)
                                }
                                value={productToRender.order}
                              />
                            </td>
                            <td id={`per_head_${i}`}>
                              {productToRender.event_count > 0
                                ? productToRender.per_head?.toFixed(5)
                                : ""}
                            </td>
                            <td id={`need_per_head_${i}`}>
                              {productToRender.event_count > 0 &&
                              forecastBasis.attendance
                                ? parseInt(
                                    productToRender.per_head *
                                      forecastBasis.attendance
                                  )
                                : ""}
                            </td>
                            <td
                              id={`order_per_head_${i}`}
                              className="pr-10 bg-blue-200"
                            >
                              {productToRender.event_count > 0 &&
                              forecastBasis.attendance
                                ? parseInt(
                                    productToRender.per_head *
                                      forecastBasis.attendance
                                  ) -
                                  productToRender.order -
                                  productToRender.currently_available
                                : ""}
                            </td>
                          </>
                        )}
                        {sortFilter === "per_show" && (
                          <>
                            <td>
                              <SmallInput
                                red_bg
                                yellow_bg={
                                  productToRender.currently_available <
                                  productToRender.per_show *
                                    forecastBasis.number_of_shows
                                }
                                name="current"
                                id={`current_${i}`}
                                onChange={(event) =>
                                  handleInputChange(event, productToRender)
                                }
                                value={productToRender.currently_available}
                              />
                            </td>
                            <td>
                              <SmallInput
                                name="order"
                                id={`order_${i}`}
                                onChange={(event) =>
                                  handleInputChange(event, productToRender)
                                }
                                value={productToRender.order}
                              />
                            </td>
                            <td id={`per_show_${i}`}>
                              {productToRender.event_count > 0
                                ? productToRender.per_show?.toFixed(3)
                                : ""}
                            </td>{" "}
                            <td id={`shows_left_${i}`}>
                              {productToRender.per_show > 0
                                ? (
                                    productToRender.current /
                                    productToRender.per_show
                                  )?.toFixed(1)
                                : ""}
                            </td>
                            <td id={`need_per_show_${i}`}>
                              {productToRender.event_count > 0 &&
                              forecastBasis.number_of_shows
                                ? parseInt(
                                    productToRender.per_show *
                                      forecastBasis.number_of_shows
                                  )
                                : ""}
                            </td>
                            <td
                              id={`order_per_show_${i}`}
                              className="pr-10 bg-blue-200"
                            >
                              {productToRender.event_count > 0 &&
                              forecastBasis.number_of_shows
                                ? parseInt(
                                    productToRender.per_show *
                                      forecastBasis.number_of_shows
                                  ) -
                                  productToRender.order -
                                  productToRender.currently_available
                                : ""}
                            </td>
                          </>
                        )}
                        {sortFilter === "gross_sales" && (
                          <>
                            <td>
                              <SmallInput
                                red_bg
                                yellow_bg={
                                  productToRender.currently_available <
                                  productToRender.per_gross *
                                    forecastBasis.gross_sales
                                }
                                name="current"
                                id={`current_${i}`}
                                onChange={(event) =>
                                  handleInputChange(event, productToRender)
                                }
                                value={productToRender.currently_available}
                              />
                            </td>
                            <td>
                              <SmallInput
                                name="order"
                                id={`order_${i}`}
                                onChange={(event) =>
                                  handleInputChange(event, productToRender)
                                }
                                value={productToRender.order}
                              />
                            </td>
                            <td id={`per_gross_${i}`}>
                              {productToRender.event_count > 0
                                ? productToRender.per_gross?.toFixed(5)
                                : ""}
                            </td>
                            <td id={`need_per_gross_${i}`}>
                              {productToRender.event_count > 0 &&
                              forecastBasis.gross_sales
                                ? parseInt(
                                    productToRender.per_gross *
                                      forecastBasis.gross_sales
                                  )
                                : ""}
                            </td>
                            <td
                              id={`order_per_gross_${i}`}
                              className="pr-10 bg-blue-200"
                            >
                              {productToRender.event_count > 0 &&
                              forecastBasis.gross_sales
                                ? parseInt(
                                    productToRender.per_gross *
                                      forecastBasis.gross_sales
                                  ) -
                                  productToRender.order -
                                  productToRender.currently_available
                                : ""}
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </form>
          </div>
        ) : (
          <div className="mx-12 pb-16">
            {resultsLoading ? (
              <>Results are loading...</>
            ) : (
              <> There is no sales history over this date range</>
            )}
          </div>
        )}
      </div>
    );
  };

  const printTable = () => {
    return (
      <form ref={printRef}>
        <table>
          <thead className="text-xs font-light">
            <tr className="w-full text-center border-b-2 h-11">
              <td className="w-1/3 text-left pl-6">PRODUCT</td>
              <td className="w-1/6">CURRENT</td>
              <td className="w-1/6">ON ORDER</td>
              {sortFilter === "per_head" && (
                <td className="w-1/6">PRODUCT SALES/HEAD</td>
              )}
              {sortFilter === "per_show" && (
                <>
                  <td className="w-1/6">PRODUCT SALES/SHOW</td>
                  <td className="w-1/6">SHOWS LEFT</td>
                </>
              )}
              {sortFilter === "gross_sales" && (
                <td className="w-1/6">PRODUCT SALES/GROSS</td>
              )}
              <td className="w-1/6">FORECASTED NEED</td>
              <td className="w-1/6 pr-10">ORDER</td>
            </tr>
          </thead>
          <tbody>
            {productsToRender.map((product, i) => {
              const productToRender = alterSalesObj[product.id] || product;
              return (
                <tr
                  key={productToRender.id}
                  className={cn("border-b text-sm h-11 text-center", {
                    "bg-yellow-50": alterSalesObj[product.id],
                    "bg-gray-100": productsToRender[i].productNumber % 2 === 1,
                  })}
                >
                  {productToRender.product_size ? (
                    <td className="text-left pl-6">
                      {productToRender.name} - {productToRender.product_size}
                    </td>
                  ) : (
                    <td className="text-left pl-6">{productToRender.name}</td>
                  )}
                  {sortFilter === "per_head" && (
                    <>
                      <td>{productToRender.currently_available}</td>
                      <td>{productToRender.order}</td>
                      <td id={`per_head_${i}`}>
                        {productToRender.event_count > 0
                          ? productToRender.per_head?.toFixed(5)
                          : ""}
                      </td>
                      <td id={`need_per_head_${i}`}>
                        {productToRender.event_count > 0 &&
                        forecastBasis.attendance
                          ? parseInt(
                              productToRender.per_head *
                                forecastBasis.attendance
                            )
                          : ""}
                      </td>
                      <td id={`order_per_head_${i}`} className="pr-10">
                        {productToRender.event_count > 0 &&
                        forecastBasis.attendance
                          ? parseInt(
                              productToRender.per_head *
                                forecastBasis.attendance
                            ) -
                            productToRender.order -
                            productToRender.currently_available
                          : ""}
                      </td>
                    </>
                  )}
                  {sortFilter === "per_show" && (
                    <>
                      <td>{productToRender.currently_available}</td>
                      <td>{productToRender.order}</td>
                      <td id={`per_show_${i}`}>
                        {productToRender.event_count > 0
                          ? productToRender.per_show?.toFixed(3)
                          : ""}
                      </td>
                      <td id={`per_show_${i}`}>
                        {productToRender.per_show > 0
                          ? (
                              productToRender.current / productToRender.per_show
                            )?.toFixed(1)
                          : ""}
                      </td>

                      <td id={`need_per_show_${i}`}>
                        {productToRender.event_count > 0 &&
                        forecastBasis.number_of_shows
                          ? parseInt(
                              (
                                productToRender.per_show *
                                forecastBasis.number_of_shows
                              )?.toFixed(3)
                            )
                          : ""}
                      </td>
                      <td id={`order_per_show_${i}`} className="pr-10">
                        {productToRender.event_count > 0 &&
                        forecastBasis.number_of_shows
                          ? parseInt(
                              productToRender.per_show *
                                forecastBasis.number_of_shows
                            ) -
                            productToRender.order -
                            productToRender.currently_available
                          : ""}
                      </td>
                    </>
                  )}
                  {sortFilter === "gross_sales" && (
                    <>
                      <td>{productToRender.currently_available}</td>
                      <td>{productToRender.order}</td>
                      <td id={`per_gross_${i}`}>
                        {productToRender.event_count > 0
                          ? productToRender.per_gross?.toFixed(5)
                          : ""}
                      </td>
                      <td id={`need_per_gross_${i}`}>
                        {productToRender.event_count > 0 &&
                        forecastBasis.gross_sales
                          ? parseInt(
                              productToRender.per_gross *
                                forecastBasis.gross_sales
                            )
                          : ""}
                      </td>
                      <td id={`order_per_gross_${i}`} className="pr-10">
                        {productToRender.event_count > 0 &&
                        forecastBasis.gross_sales
                          ? parseInt(
                              productToRender.per_gross *
                                forecastBasis.gross_sales
                            ) -
                            productToRender.order -
                            productToRender.currently_available
                          : ""}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </form>
    );
  };

  return (
    <InventoryLayout>
      <div className="text-black bg-gray-100 pt-2">
        <div className="mt-8 mx-8 text-black bg-white rounded-lg border">
          <div className="mx-10 pt-8 pb-10 font-medium text-sm text-black">
            <div className="flex justify-end text-sm">
              {formatPrint && (
                <>
                  <Button
                    className="button button-white mr-2"
                    text="Edit"
                    onClick={() => handleFormatPrint()}
                  ></Button>
                  <ReactToPrint
                    trigger={() => (
                      <Button className="button" text="Print"></Button>
                    )}
                    content={() => printRef.current}
                  />
                </>
              )}
              {!formatPrint && (
                <>
                  <Button
                    className="button button-white"
                    text="Export"
                    onClick={() =>
                      exportToCSV(
                        prodsToExport,
                        `${filterData.artist_name}_forecast`
                      )
                    }
                  ></Button>
                  <div className="ml-2">
                    <Button
                      className="button"
                      text="Format Print"
                      onClick={() => handleFormatPrint()}
                    ></Button>
                  </div>
                </>
              )}
            </div>
            <div className="-mt-9 text-black text-lg mb-4">
              Inventory Forecast
            </div>
            <div className="flex text-black items-end pb-2">
              <div className="w-1/4 pr-12">Forecast variable</div>
              {sortFilter === "per_head" && (
                <div className="w-1/8">Attendance</div>
              )}
              {sortFilter === "per_show" && (
                <div className="w-1/8">Number of shows</div>
              )}
              {sortFilter === "gross_sales" && (
                <div className="w-1/8">Gross sales</div>
              )}
              <div className="w-1/4 pl-12">Date range/show</div>
            </div>
            <div className="flex">
              <div className="w-1/4 pr-12">
                <Select
                  id="sort_filter"
                  className="text-xs font-light"
                  placeholder="Select forecast variable"
                  options={sortOptions}
                  onChange={handleSortSelect}
                  value={sortOptions?.filter(
                    (each) => each.value === sortFilter
                  )}
                />
              </div>
              {sortFilter === "per_head" && (
                <div className="w-1/8">
                  <Input
                    name="attendance"
                    onChange={(e) => handleForecastBasisChange(e)}
                    value={forecastBasis.attendance}
                  />
                </div>
              )}
              {sortFilter === "per_show" && (
                <div className="w-1/8">
                  <Input
                    name="number_of_shows"
                    onChange={(e) => handleForecastBasisChange(e)}
                    value={forecastBasis.number_of_shows}
                  />
                </div>
              )}
              {sortFilter === "gross_sales" && (
                <div className="w-1/8">
                  <Input
                    name="gross_sales"
                    onChange={(e) => handleForecastBasisChange(e)}
                    value={forecastBasis.gross_sales}
                  />
                </div>
              )}
              <EventFilter
                events={events}
                setEvents={setEvents}
                filterData={filterData}
                eventIds={eventIds}
                eventFilterDate={eventFilterDate}
                setEventFilterDate={setEventFilterDate}
                setEventIds={setEventIds}
                setFilterData={setFilterData}
              ></EventFilter>
            </div>
            <div className="mt-8 text-sm">
              <div className="flex">
                <div className="absolute mt-2 ml-2 text-black">
                  <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
                </div>
                <div className="w-1/4">
                  <Input
                    onChange={handleSearchChange}
                    value={searchTimeout}
                    placeholder="Search for products"
                    bigPaddingLeft
                  />
                </div>
                <Select
                  id="category_filter"
                  className="text-xs font-light w-1/4 ml-2"
                  placeholder="Filter by category"
                  options={categoryOptions}
                  onChange={handleCategorySelect}
                  value={categoryOptions?.filter(
                    (each) => each.value === allProdsCategoryFilter
                  )}
                />
                {(allProdsSearchFilter || allProdsCategoryFilter) && (
                  <button
                    onClick={resetFilters}
                    className="text-blue-500 font-normal italic text-sm ml-2 pl-2"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>
          </div>
          {filterData.artist_id ? (
            <>{!formatPrint ? renderAdjustTable() : printTable()}</>
          ) : (
            renderSelectArtistMessage()
          )}
        </div>
        {splitItemsList.length > 0 && !formatPrint && (
          <>
            {Object.keys(alterSalesObj).length > 0 && (
              <span className="flex justify-center mt-4">
                <Button
                  className="button button-white"
                  onClick={clear}
                  text="Clear"
                ></Button>
              </span>
            )}
            {renderFooter()}
          </>
        )}
      </div>
    </InventoryLayout>
  );
};

export default ForecastInventory;
